export const languages = {
  en: 'English',
  tr: 'Türkçe',
  uk: 'Українська',
  ru: 'Русский',
  hi: 'हिन्दी'
} as const;

export type Language = keyof typeof languages;

export const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.features': 'Features',
    'nav.pricing': 'Pricing',
    'nav.signin': 'Sign in',
    'nav.start': 'Start Learning',
    
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome back',
    'dashboard.overview': 'Overview',
    'dashboard.processes': 'My Processes',
    'dashboard.documents': 'Documents',
    'dashboard.checklists': 'Checklists',
    'dashboard.profile': 'Profile',
    'dashboard.logout': 'Logout',
    
    // Processes
    'process.checklist': 'Checklist',
    'process.documents': 'Documents',
    'process.links': 'Official Links',
    'process.tips': 'City Tips',
    'process.templates': 'Templates',
    'process.issues': 'Common Issues',
    'process.progress': 'Progress',
    'process.continue': 'Continue',
    'process.completed': 'Completed',
    'process.inprogress': 'In Progress',
    'process.notstarted': 'Not Started',
    
    // Process Names
    'process.pesel.name': 'PESEL Registration',
    'process.pesel.description': 'Polish personal identification number',
    'process.residence.name': 'Residence Permit',
    'process.residence.description': 'Legal residence status in Poland',
    'process.nip.name': 'NIP (Tax ID)',
    'process.nip.description': 'Polish tax identification number',
    'process.bank.name': 'Bank Account',
    'process.bank.description': 'Polish bank account for residents',
    'process.company.name': 'Company Registration',
    'process.company.description': 'Business registration in Poland',
    
    // Common
    'common.free': 'Free',
    'common.download': 'Download',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.view': 'View',
    'common.upload': 'Upload',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.all': 'All',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    
    // Landing Page
    'hero.title': 'Polish Paperwork Made Simple',
    'hero.subtitle': 'Step-by-step guides that generate personal checklists for PESEL, NIP, residence permits, and more. Built for expats in Poland.',
    'hero.cta': 'Get Your Personal Guide',
    'hero.price': 'One-time payment • Lifetime access',
    
    // Features
    'features.title': 'What You\'ll Get',
    'features.subtitle': 'Everything you need to successfully complete your Polish paperwork.',
    
    // How It Works
    'howitworks.title': 'How It Works',
    'howitworks.step1': 'Choose Your Process',
    'howitworks.step2': 'Get Your Checklist',
    'howitworks.step3': 'Complete Successfully',
    
    // Cities
    'cities.title': 'City-Specific Guides',
    'cities.subtitle': 'Tailored information for major Polish cities',
    
    // Pricing
    'pricing.title': 'Simple, One-Time Payment',
    'pricing.subtitle': 'Get lifetime access to all guides and updates',
    'pricing.price': '€49',
    'pricing.cta': 'Get Lifetime Access',
    'pricing.features': 'What\'s included:',
    
    // Auth
    'auth.login.title': 'Welcome Back',
    'auth.login.subtitle': 'Sign in to your account',
    'auth.login.email': 'Email',
    'auth.login.password': 'Password',
    'auth.login.remember': 'Remember me',
    'auth.login.forgot': 'Forgot password?',
    'auth.login.signin': 'Sign In',
    'auth.login.noaccount': 'Don\'t have an account?',
    'auth.login.signup': 'Sign up',
    
    'auth.register.title': 'Create Account',
    'auth.register.subtitle': 'Start your Polish paperwork journey',
    'auth.register.name': 'Full Name',
    'auth.register.email': 'Email',
    'auth.register.password': 'Password',
    'auth.register.city': 'City in Poland',
    'auth.register.terms': 'I agree to the Terms of Service and Privacy Policy',
    'auth.register.signup': 'Create Account',
    'auth.register.hasaccount': 'Already have an account?',
    'auth.register.signin': 'Sign in',
  },
  tr: {
    // Navigation
    'nav.home': 'Ana Sayfa',
    'nav.features': 'Özellikler',
    'nav.pricing': 'Fiyatlandırma',
    'nav.signin': 'Giriş Yap',
    'nav.start': 'Öğrenmeye Başla',
    
    // Dashboard
    'dashboard.title': 'Kontrol Paneli',
    'dashboard.welcome': 'Tekrar hoş geldiniz',
    'dashboard.overview': 'Genel Bakış',
    'dashboard.processes': 'İşlemlerim',
    'dashboard.documents': 'Belgeler',
    'dashboard.checklists': 'Kontrol Listeleri',
    'dashboard.profile': 'Profil',
    'dashboard.logout': 'Çıkış Yap',
    
    // Processes
    'process.checklist': 'Kontrol Listesi',
    'process.documents': 'Belgeler',
    'process.links': 'Resmi Linkler',
    'process.tips': 'Şehir İpuçları',
    'process.templates': 'Şablonlar',
    'process.issues': 'Yaygın Sorunlar',
    'process.progress': 'İlerleme',
    'process.continue': 'Devam Et',
    'process.completed': 'Tamamlandı',
    'process.inprogress': 'Devam Ediyor',
    'process.notstarted': 'Başlamadı',
    
    // Process Names
    'process.pesel.name': 'PESEL Kaydı',
    'process.pesel.description': 'Polonya kişisel kimlik numarası',
    'process.residence.name': 'İkamet İzni',
    'process.residence.description': 'Polonya\'da yasal ikamet statüsü',
    'process.nip.name': 'NIP (Vergi Kimlik)',
    'process.nip.description': 'Polonya vergi kimlik numarası',
    'process.bank.name': 'Banka Hesabı',
    'process.bank.description': 'Polonya\'da banka hesabı',
    'process.company.name': 'Şirket Kaydı',
    'process.company.description': 'Polonya\'da işletme kaydı',
    
    // Common
    'common.free': 'Ücretsiz',
    'common.download': 'İndir',
    'common.save': 'Kaydet',
    'common.cancel': 'İptal',
    'common.edit': 'Düzenle',
    'common.delete': 'Sil',
    'common.view': 'Görüntüle',
    'common.upload': 'Yükle',
    'common.search': 'Ara',
    'common.filter': 'Filtrele',
    'common.all': 'Tümü',
    'common.loading': 'Yükleniyor...',
    'common.error': 'Hata',
    'common.success': 'Başarılı',
    
    // Landing Page
    'hero.title': 'Polonya Evrakları Artık Kolay',
    'hero.subtitle': 'PESEL, NIP, ikamet izni ve daha fazlası için kişisel kontrol listeleri oluşturan adım adım rehberler. Polonya\'daki yabancılar için yapıldı.',
    'hero.cta': 'Kişisel Rehberinizi Alın',
    'hero.price': 'Tek seferlik ödeme • Ömür boyu erişim',
    
    // Features
    'features.title': 'Neler Alacaksınız',
    'features.subtitle': 'Polonya evraklarınızı başarıyla tamamlamak için ihtiyacınız olan her şey.',
    
    // How It Works
    'howitworks.title': 'Nasıl Çalışır',
    'howitworks.step1': 'İşleminizi Seçin',
    'howitworks.step2': 'Kontrol Listenizi Alın',
    'howitworks.step3': 'Başarıyla Tamamlayın',
    
    // Cities
    'cities.title': 'Şehre Özel Rehberler',
    'cities.subtitle': 'Büyük Polonya şehirleri için özel bilgiler',
    
    // Pricing
    'pricing.title': 'Basit, Tek Seferlik Ödeme',
    'pricing.subtitle': 'Tüm rehberlere ve güncellemelere ömür boyu erişim',
    'pricing.price': '€49',
    'pricing.cta': 'Ömür Boyu Erişim Al',
    'pricing.features': 'Dahil olanlar:',
    
    // Auth
    'auth.login.title': 'Tekrar Hoş Geldiniz',
    'auth.login.subtitle': 'Hesabınıza giriş yapın',
    'auth.login.email': 'E-posta',
    'auth.login.password': 'Şifre',
    'auth.login.remember': 'Beni hatırla',
    'auth.login.forgot': 'Şifremi unuttum?',
    'auth.login.signin': 'Giriş Yap',
    'auth.login.noaccount': 'Hesabınız yok mu?',
    'auth.login.signup': 'Kayıt ol',
    
    'auth.register.title': 'Hesap Oluştur',
    'auth.register.subtitle': 'Polonya evrak yolculuğunuzu başlatın',
    'auth.register.name': 'Ad Soyad',
    'auth.register.email': 'E-posta',
    'auth.register.password': 'Şifre',
    'auth.register.city': 'Polonya\'daki Şehir',
    'auth.register.terms': 'Hizmet Şartları ve Gizlilik Politikasını kabul ediyorum',
    'auth.register.signup': 'Hesap Oluştur',
    'auth.register.hasaccount': 'Zaten hesabınız var mı?',
    'auth.register.signin': 'Giriş yap',
  },
  uk: {
    // Navigation
    'nav.home': 'Головна',
    'nav.features': 'Особливості',
    'nav.pricing': 'Ціни',
    'nav.signin': 'Увійти',
    'nav.start': 'Почати навчання',
    
    // Dashboard
    'dashboard.title': 'Панель керування',
    'dashboard.welcome': 'З поверненням',
    'dashboard.overview': 'Огляд',
    'dashboard.processes': 'Мої процеси',
    'dashboard.documents': 'Документи',
    'dashboard.checklists': 'Контрольні списки',
    'dashboard.profile': 'Профіль',
    'dashboard.logout': 'Вийти',
    
    // Processes
    'process.checklist': 'Контрольний список',
    'process.documents': 'Документи',
    'process.links': 'Офіційні посилання',
    'process.tips': 'Поради для міста',
    'process.templates': 'Шаблони',
    'process.issues': 'Поширені проблеми',
    'process.progress': 'Прогрес',
    'process.continue': 'Продовжити',
    'process.completed': 'Завершено',
    'process.inprogress': 'В процесі',
    'process.notstarted': 'Не розпочато',
    
    // Process Names
    'process.pesel.name': 'Реєстрація PESEL',
    'process.pesel.description': 'Польський особистий ідентифікаційний номер',
    'process.residence.name': 'Дозвіл на проживання',
    'process.residence.description': 'Легальний статус проживання в Польщі',
    'process.nip.name': 'NIP (Податковий ID)',
    'process.nip.description': 'Польський податковий ідентифікаційний номер',
    'process.bank.name': 'Банківський рахунок',
    'process.bank.description': 'Польський банківський рахунок',
    'process.company.name': 'Реєстрація компанії',
    'process.company.description': 'Реєстрація бізнесу в Польщі',
    
    // Common
    'common.free': 'Безкоштовно',
    'common.download': 'Завантажити',
    'common.save': 'Зберегти',
    'common.cancel': 'Скасувати',
    'common.edit': 'Редагувати',
    'common.delete': 'Видалити',
    'common.view': 'Переглянути',
    'common.upload': 'Завантажити',
    'common.search': 'Пошук',
    'common.filter': 'Фільтр',
    'common.all': 'Всі',
    'common.loading': 'Завантаження...',
    'common.error': 'Помилка',
    'common.success': 'Успіх',
  },
  ru: {
    // Navigation
    'nav.home': 'Главная',
    'nav.features': 'Особенности',
    'nav.pricing': 'Цены',
    'nav.signin': 'Войти',
    'nav.start': 'Начать обучение',
    
    // Dashboard
    'dashboard.title': 'Панель управления',
    'dashboard.welcome': 'Добро пожаловать',
    'dashboard.overview': 'Обзор',
    'dashboard.processes': 'Мои процессы',
    'dashboard.documents': 'Документы',
    'dashboard.checklists': 'Контрольные списки',
    'dashboard.profile': 'Профиль',
    'dashboard.logout': 'Выйти',
    
    // Processes
    'process.checklist': 'Контрольный список',
    'process.documents': 'Документы',
    'process.links': 'Официальные ссылки',
    'process.tips': 'Советы по городу',
    'process.templates': 'Шаблоны',
    'process.issues': 'Частые проблемы',
    'process.progress': 'Прогресс',
    'process.continue': 'Продолжить',
    'process.completed': 'Завершено',
    'process.inprogress': 'В процессе',
    'process.notstarted': 'Не начато',
    
    // Process Names
    'process.pesel.name': 'Регистрация PESEL',
    'process.pesel.description': 'Польский личный идентификационный номер',
    'process.residence.name': 'Разрешение на проживание',
    'process.residence.description': 'Легальный статус проживания в Польше',
    'process.nip.name': 'NIP (Налоговый ID)',
    'process.nip.description': 'Польский налоговый идентификационный номер',
    'process.bank.name': 'Банковский счет',
    'process.bank.description': 'Польский банковский счет',
    'process.company.name': 'Регистрация компании',
    'process.company.description': 'Регистрация бизнеса в Польше',
    
    // Common
    'common.free': 'Бесплатно',
    'common.download': 'Скачать',
    'common.save': 'Сохранить',
    'common.cancel': 'Отмена',
    'common.edit': 'Редактировать',
    'common.delete': 'Удалить',
    'common.view': 'Просмотр',
    'common.upload': 'Загрузить',
    'common.search': 'Поиск',
    'common.filter': 'Фильтр',
    'common.all': 'Все',
    'common.loading': 'Загрузка...',
    'common.error': 'Ошибка',
    'common.success': 'Успех',
  },
  hi: {
    // Navigation
    'nav.home': 'होम',
    'nav.features': 'विशेषताएं',
    'nav.pricing': 'मूल्य निर्धारण',
    'nav.signin': 'साइन इन',
    'nav.start': 'सीखना शुरू करें',
    
    // Dashboard
    'dashboard.title': 'डैशबोर्ड',
    'dashboard.welcome': 'वापसी पर स्वागत है',
    'dashboard.overview': 'अवलोकन',
    'dashboard.processes': 'मेरी प्रक्रियाएं',
    'dashboard.documents': 'दस्तावेज़',
    'dashboard.checklists': 'चेकलिस्ट',
    'dashboard.profile': 'प्रोफ़ाइल',
    'dashboard.logout': 'लॉग आउट',
    
    // Processes
    'process.checklist': 'चेकलिस्ट',
    'process.documents': 'दस्तावेज़',
    'process.links': 'आधिकारिक लिंक',
    'process.tips': 'शहर की युक्तियां',
    'process.templates': 'टेम्प्लेट',
    'process.issues': 'सामान्य समस्याएं',
    'process.progress': 'प्रगति',
    'process.continue': 'जारी रखें',
    'process.completed': 'पूर्ण',
    'process.inprogress': 'प्रगति में',
    'process.notstarted': 'शुरू नहीं हुआ',
    
    // Process Names
    'process.pesel.name': 'PESEL पंजीकरण',
    'process.pesel.description': 'पोलिश व्यक्तिगत पहचान संख्या',
    'process.residence.name': 'निवास परमिट',
    'process.residence.description': 'पोलैंड में कानूनी निवास स्थिति',
    'process.nip.name': 'NIP (कर ID)',
    'process.nip.description': 'पोलिश कर पहचान संख्या',
    'process.bank.name': 'बैंक खाता',
    'process.bank.description': 'पोलिश बैंक खाता',
    'process.company.name': 'कंपनी पंजीकरण',
    'process.company.description': 'पोलैंड में व्यापार पंजीकरण',
    
    // Common
    'common.free': 'मुफ़्त',
    'common.download': 'डाउनलोड',
    'common.save': 'सेव',
    'common.cancel': 'रद्द करें',
    'common.edit': 'संपादित करें',
    'common.delete': 'हटाएं',
    'common.view': 'देखें',
    'common.upload': 'अपलोड',
    'common.search': 'खोजें',
    'common.filter': 'फ़िल्टर',
    'common.all': 'सभी',
    'common.loading': 'लोड हो रहा है...',
    'common.error': 'त्रुटि',
    'common.success': 'सफलता',
  }
} as const;

export function useTranslation(language: Language = 'en') {
  const t = (key: string): string => {
    return (translations[language] as any)[key] || (translations.en as any)[key] || key;
  };

  return { t, language };
}
